import React from "react";
import type { ComponentType } from "react";
import CompanyProfile from "./employerDashboard/profile/CompanyProfile";
import IndividualProfile from "./individualDashboard/profile/IndividualProfile";
import UserManagement from "./employerDashboard/userManagement/UserManagement";
import ProcessContributions from "./employerDashboard/processContributions/ProcessContributions";
import ProcessOneTimeContributions from "./employerDashboard/processContributions/ProcessOneTimeContributions";
import PayrollWorkflow from "./employerDashboard/processContributions/payrollWorkflow/PayrollWorkflow";
import ApproveOneTimePayrollWorkflow from "./employerDashboard/processContributions/ApproveOneTimePayrollWorkflow";
import EmployeeLinkRequestWorkflow from "pages/dashboards/employerDashboard/profile/employeeLinkRequestWorkflow/EmployeeLinkRequestWorkflow";
import DashboardContainer from "pages/dashboards/employerDashboard/dashboard/DashboardContainer";
import IndividualDashboard from "./individualDashboard/dashboard/IndividualDashboard";
import IndividualStatements from "./individualDashboard/documents/IndividualStatements";
import IndividualContributions from "./individualDashboard/contributions/IndividualContributions";
import Beneficiaries from "./individualDashboard/beneficiaries/Beneficiaries";
import Transactions from "./individualDashboard/transactions/Transactions";
import AdminSignup from "pages/signUp/iconAdmin/adminSignup";
import Employers from "./adminDashboard/employers/Employers";
import Employer from "./adminDashboard/employers/Employer";
import AdminTransactions from "./adminDashboard/transactions/AdminTransactions";
import AdminReports from "pages/dashboards/adminDashboard/reports/AdminReports";
import CustomReportForm from "pages/dashboards/adminDashboard/reports/CustomReportForm";
import Approval from "pages/admin/Approval";
import ApprovalTable from "pages/admin/ApprovalTable";
import PayrollHistory from "pages/dashboards/employerDashboard/processContributions/PayrollHistory";
import SupportPage from "pages/dashboards/individualDashboard/support/SupportPage";
import Users from "./adminDashboard/users/Users";
import User from "./adminDashboard/users/User";
import ReferralForm from "../../components/ReferralForm";
import SystemLimits from "./adminDashboard/SystemLimits";
import CustomReports from "pages/dashboards/adminDashboard/reports/CustomReports";
import EmployerDocuments from "./employerDashboard/documents/EmployerDocuments";
import Workflows from "pages/dashboards/adminDashboard/workflows/Workflows";
import IndividualRolloverForm from "../signUp/individual/IndividualRolloverForm";
import DistributionWorkflow from "pages/dashboards/individualDashboard/support/distributions/DistributionWorkflow";
import {
  employerAdminRole,
  regularAdminRole,
  salesAgentRole,
  superAdminRole,
} from "store/selectors/admin";

import EmployerOrders from "pages/dashboards/adminDashboard/employerOrders/EmployerOrders";
import * as FiIcons from "react-icons/fi";
import { some } from "lodash";
import CloseAccountWorkflow from "./individualDashboard/support/closeAccount/CloseAccountWorkflow";
import PaychexDashboard from "./adminDashboard/paychexDashboard/PaychexDashboard";
import { TbLetterP } from "react-icons/tb";

/**
 * Source-of-truth shape for every route-config object in this registry. It is
 * the superset consumed by SideBar / SubNav / useDashboardRoutes /
 * renderDashboardRoutes (each models only the subset of fields it reads). The
 * registry is heterogeneous across the individual / employer / admin portals,
 * so `component` is intentionally `ComponentType<any>` (matching
 * `routes/renderDashboardRoutes.DashboardRoute`) — narrowing it
 * contravariantly rejects connected pages whose own-props are all optional.
 */
interface DashboardRoute {
  path: string;
  layout: string;
  component: ComponentType<any>;
  name?: string;
  icon?: React.ReactNode;
  mini?: string;
  state?: string;
  collapse?: boolean;
  hidden?: boolean;
  hiddenChildRoutes?: boolean;
  exact?: boolean;
  roleRestrictions?: string[];
  views?: DashboardRoute[];
}

export const employerRoutes: DashboardRoute[] = [
  {
    path: "/dashboard",
    layout: "/employer",
    name: "Dashboard",
    icon: (
      <FiIcons.FiHome
        stroke="#FFFFFF"
        // PRESERVED BUG (conversion-only): `ill` is a typo for `fill` in the
        // original JSX — it is not a valid SVG attribute, so it renders as a
        // no-op. Kept verbatim to avoid changing behavior.
        // @ts-expect-error react-icons IconBaseProps has no `ill` attribute
        ill="#30374e"
        color="#30374e"
      />
    ),
    mini: "D",
    component: DashboardContainer,
    hiddenChildRoutes: true,
    views: [
      {
        path: "/dashboard",
        component: DashboardContainer,
        layout: "/employer",
        name: "Dashboard",
      },
      {
        path: "/dashboard/referral",
        component: ReferralForm,
        layout: "/employer",
        hidden: true,
      },
    ],
  },
  {
    path: "/dashboard/company",
    layout: "/employer",
    collapse: true,
    name: "Company",
    icon: <FiIcons.FiBookOpen />,
    mini: "P",
    state: "openCompProfile",
    component: CompanyProfile,
    views: [
      {
        path: "/dashboard/company/administrators",
        layout: "/employer",
        name: "Administrators",
        component: CompanyProfile,
      },
      {
        path: "/dashboard/company/bank",
        layout: "/employer",
        component: CompanyProfile,
        name: "Bank Info",
      },
      {
        path: "/dashboard/company/group",
        layout: "/employer",
        component: CompanyProfile,
        name: "Group Settings",
      },
      {
        path: "/dashboard/company/group/:id/review-employees",
        layout: "/employer",
        component: EmployeeLinkRequestWorkflow,
        hidden: true,
      },
      {
        path: "/dashboard/company/integrations",
        layout: "/employer",
        component: CompanyProfile,
        name: "Integrations",
      },
    ],
  },
  {
    path: "/dashboard/users/employees",
    layout: "/employer",
    name: "Employees",
    icon: <FiIcons.FiUsers />,
    mini: "UM",
    collapse: true,
    component: UserManagement,
    views: [
      {
        path: "/dashboard/users/employees",
        component: UserManagement,
        layout: "/employer",
        name: "Employee Invitations",
      },
      {
        path: "/dashboard/users/employees/upload",
        component: UserManagement,
        layout: "/employer",
        name: "Upload Roster",
      },
      {
        path: "/dashboard/users/employees/upload-single",
        component: UserManagement,
        layout: "/employer",
        hidden: true,
        name: "Upload Single",
      },
    ],
  },
  {
    layout: "/employer",
    path: "/dashboard/contributions",
    name: "Contributions",
    icon: <FiIcons.FiRepeat />,
    mini: "PC",
    collapse: true,
    component: ProcessContributions,
    state: "openProcessContributions",
    views: [
      {
        path: "/dashboard/contributions",
        component: ProcessContributions,
        layout: "/employer",
        name: "Payrolls",
      },
      {
        path: "/dashboard/contributions/one-time",
        component: ProcessOneTimeContributions,
        layout: "/employer",
        name: "Process One-Time",
      },
      {
        path: "/dashboard/contributions/history",
        component: PayrollHistory,
        layout: "/employer",
        name: "History",
      },
      {
        path: "/dashboard/contributions/:id/approval",
        component: PayrollWorkflow,
        layout: "/employer",
        hidden: true,
      },
      {
        path: "/dashboard/contributions/:id/one-time",
        component: ApproveOneTimePayrollWorkflow,
        layout: "/employer",
        hidden: true,
      },
    ],
  },
  {
    path: "/dashboard/documents",
    name: "Documents",
    icon: <FiIcons.FiFileText />,
    hidden: true,
    mini: "S",
    layout: "/employer",
    component: EmployerDocuments,
  },
];

export const individualRoutes: DashboardRoute[] = [
  {
    path: "/dashboard",
    layout: "/individual",
    name: "Dashboard",
    component: IndividualDashboard,
  },
  {
    path: "/dashboard/contributions",
    layout: "/individual",
    name: "Contributions",
    component: IndividualContributions,
  },
  {
    path: "/dashboard/transactions",
    layout: "/individual",
    name: "Transactions",
    component: Transactions,
  },
  {
    path: "/dashboard/documents",
    name: "Documents",
    hidden: true,
    layout: "/individual",
    component: IndividualStatements,
  },

  {
    path: "/dashboard/beneficiaries",
    layout: "/individual",
    name: "Beneficiaries",
    component: Beneficiaries,
  },
  {
    path: "/dashboard/profile",
    collapse: true,
    layout: "/individual",
    name: "Profile",
    state: "openProfile",
    mini: "P",
    hidden: true,
    component: IndividualProfile,
    views: [
      {
        path: "/dashboard/profile/info",
        component: IndividualProfile,
        layout: "/individual",
        name: "User Info",
      },
      {
        path: "/dashboard/profile/investment-profile",
        component: IndividualProfile,
        layout: "/individual",
        name: "Investment Profile",
      },
      {
        path: "/dashboard/profile/login-info",
        component: IndividualProfile,
        layout: "/individual",
        name: "Login Info",
      },
      {
        path: "/dashboard/profile/close-account",
        component: IndividualProfile,
        layout: "/individual",
        name: "Close Account",
      },
    ],
  },
  {
    path: "/dashboard/support",
    name: "Support",
    mini: "S",
    layout: "/individual",
    hidden: true,
    component: SupportPage,
  },
  {
    path: "/dashboard/rollover_form",
    name: "Rollover Form",
    mini: "S",
    layout: "/individual",
    hidden: true,
    component: IndividualRolloverForm,
  },
  {
    path: "/dashboard/support/distributions",
    name: "Distributions",
    mini: "S",
    layout: "/individual",
    hidden: true,
    component: DistributionWorkflow,
  },
  {
    path: "/dashboard/support/close-account",
    name: "Close Account",
    mini: "S",
    layout: "/individual",
    hidden: true,
    component: CloseAccountWorkflow,
    exact: true,
  },
];

export function getResponsiveSideBarRoutes() {
  const accessibleRoutes = [
    "/dashboard",
    "/dashboard/contributions",
    "/dashboard/transactions",
    "/dashboard/beneficiaries",
    "/dashboard/documents",
    "/dashboard/profile",
    "/dashboard/support",
  ];
  return individualRoutes
    .filter((route) => {
      return accessibleRoutes.includes(route.path);
    })
    .map((route) => {
      return {
        ...route,
        hidden: false,
      };
    });
}

export function getAccountClosedRoutes() {
  // filter routes that are available to closed account user
  const accessibleRoutes = ["/dashboard", "/dashboard/documents"];
  return individualRoutes
    .filter((route) => {
      return accessibleRoutes.includes(route.path);
    })
    .map((route) => {
      return {
        ...route,
        hidden: false,
      };
    });
}

export function getOnboardingEmployerRoutes(isOwner: boolean) {
  // filter routes that are available to closed account user
  const accessibleRoutes = ["/dashboard"];
  if (isOwner) {
    accessibleRoutes.push("/dashboard/company/administrators");
  }
  const initialRoutes = employerRoutes.filter((route) => {
    return accessibleRoutes.includes(route.path);
  });

  if (isOwner) {
    const companyRoute =
      employerRoutes.find((route) => route.path === "/dashboard/company") || {};
    const companyRouteWithOnlyAdminInvite = {
      ...companyRoute,
    } as DashboardRoute;

    // PRESERVED BUG (conversion-only): when `find` misses, `companyRoute` is the
    // `{}` fallback so `.views` is undefined and `.filter` throws at runtime.
    // The `!` is a compile-time-only non-null assertion (erased at runtime), so
    // it silences the checker without adding `?.`/`??` — the original
    // throw-on-missing behavior is preserved verbatim.
    companyRouteWithOnlyAdminInvite.views = (
      companyRoute as DashboardRoute
    ).views!.filter((view) => {
      return view.path === "/dashboard/company/administrators";
    });
    initialRoutes.push(companyRouteWithOnlyAdminInvite);
  }

  return initialRoutes;
}

export function getEmployerRoutes({
  hasRanFirstPayroll,
  hasStatePlan,
  isAdministrator,
  hasPayrollIntegration,
  hasVirtualBank,
  hasUkgPayrollIntegration,
}: {
  hasRanFirstPayroll?: boolean;
  hasStatePlan?: boolean;
  isAdministrator?: boolean;
  hasPayrollIntegration?: boolean;
  hasVirtualBank?: boolean;
  hasUkgPayrollIntegration?: boolean;
}) {
  let inaccessibleViews: string[] = [];
  if (!hasRanFirstPayroll) {
    inaccessibleViews.push("/dashboard/contributions/one-time");
  }

  if (!hasStatePlan || hasPayrollIntegration) {
    inaccessibleViews.push("/dashboard/users/employees/upload");
  }

  if (isAdministrator) {
    inaccessibleViews.push("/dashboard/company/administrators");
  }

  if (hasVirtualBank) {
    inaccessibleViews.push("/dashboard/company/bank");
  }

  if (!hasUkgPayrollIntegration) {
    inaccessibleViews.push("/dashboard/company/integrations");
  }

  return employerRoutes.map((route) => {
    if (!route.views) {
      return route;
    }
    const views = route.views.filter(
      (view) => !inaccessibleViews.includes(view.path)
    );
    return { ...route, views };
  });
}

export function getLockedUserRoutes() {
  const accessibleRoutes = ["/dashboard"];
  return individualRoutes.filter((route) =>
    accessibleRoutes.includes(route.path)
  );
}

export const adminRoutes: DashboardRoute[] = [
  {
    path: "/",
    layout: "/admin",
    name: "Admin Management",
    icon: <FiIcons.FiBookOpen />,
    mini: "A",
    component: AdminSignup,
  },
  {
    path: "/security",
    layout: "/admin",
    name: "Security",
    roleRestrictions: [superAdminRole],
    icon: <FiIcons.FiList />,
    mini: "R",
    component: SystemLimits,
    hiddenChildRoutes: true,
    views: [
      {
        path: "/security",
        component: SystemLimits,
        layout: "/admin",
      },
      {
        path: "/security/limits",
        component: SystemLimits,
        layout: "/admin",
      },
      {
        path: "/security/payrolls",
        component: SystemLimits,
        layout: "/admin",
      },
    ],
  },
  {
    path: "/transactions",
    layout: "/admin",
    name: "Transactions",
    roleRestrictions: [superAdminRole, regularAdminRole, employerAdminRole],
    icon: <FiIcons.FiList />,
    mini: "T",
    component: AdminTransactions,
    hiddenChildRoutes: true,
    views: [
      {
        path: "/transactions",
        component: AdminTransactions,
        layout: "/admin",
      },
      {
        path: "/transactions/payrolls",
        component: AdminTransactions,
        layout: "/admin",
      },
      {
        path: "/transactions/missed_payrolls",
        component: AdminTransactions,
        layout: "/admin",
      },
    ],
  },
  {
    path: "/employers",
    layout: "/admin",
    name: "Employers",
    roleRestrictions: [superAdminRole, regularAdminRole, employerAdminRole],
    icon: <FiIcons.FiBriefcase />,
    mini: "A",
    component: Employers,
    hiddenChildRoutes: true,
    views: [
      {
        path: "/employers/:id",
        component: Employer,
        layout: "/admin",
      },
      {
        path: "/employers/:id/info",
        component: Employer,
        layout: "/admin",
      },
      {
        path: "/employers/:id/documents",
        component: Employer,
        layout: "/admin",
      },
      {
        path: "/employers/:id/kyb/info",
        component: Employer,
        layout: "/admin",
      },
      {
        path: "/employers/:id/kyb/owners",
        component: Employer,
        layout: "/admin",
      },
      {
        path: "/employers/:id/kyb/summary",
        component: Employer,
        layout: "/admin",
      },
      {
        path: "/employers",
        component: Employers,
        layout: "/admin",
      },
      {
        path: "/employers/:id/groups",
        component: Employer,
        layout: "/admin",
      },
      {
        path: "/employers/:id/payrolls",
        component: Employer,
        layout: "/admin",
      },
      {
        path: "/employers/:id/missed_payrolls",
        component: Employer,
        layout: "/admin",
      },
      {
        path: "/employers/:id/roster",
        component: Employer,
        layout: "/admin",
      },
      {
        path: "/employers/:id/billing",
        component: Employer,
        layout: "/admin",
      },
      {
        path: "/employers/:id/organization",
        component: Employer,
        layout: "/admin",
      },
      {
        path: "/employers/:id/limits",
        component: Employer,
        layout: "/admin",
      },
      {
        path: "/employers/:id/ach",
        component: Employer,
        layout: "/admin",
      },
      {
        path: "/employers/:id/contribution_ability",
        component: Employer,
        layout: "/admin",
      },
      {
        path: "/employers/:id/crm",
        component: Employer,
        layout: "/admin",
      },
    ],
  },
  {
    path: "/users",
    layout: "/admin",
    name: "Users",
    icon: <FiIcons.FiUsers />,
    roleRestrictions: [superAdminRole, regularAdminRole],
    mini: "A",
    component: Users,
    hiddenChildRoutes: true,
    views: [
      {
        path: "/users/:id",
        component: User,
        layout: "/admin",
      },
      {
        path: "/users/:id/info",
        component: User,
        layout: "/admin",
      },
      {
        path: "/users/:id/transactions",
        component: User,
        layout: "/admin",
      },
      {
        path: "/users/:id/employers",
        component: User,
        layout: "/admin",
      },
      {
        path: "/users/:id/accounts",
        component: User,
        layout: "/admin",
      },
      {
        path: "/users/:id/contribution_ability",
        component: User,
        layout: "/admin",
      },
      {
        path: "/users/:id/limits",
        component: User,
        layout: "/admin",
      },
      {
        path: "/users/:id/rollovers",
        component: User,
        layout: "/admin",
      },
      {
        path: "/users/:id/yellowpath",
        component: User,
        layout: "/admin",
      },
      {
        path: "/users",
        component: Users,
        layout: "/admin",
      },
    ],
  },
  {
    path: "/reports",
    layout: "/admin",
    name: "Reports",
    roleRestrictions: [superAdminRole],
    icon: <FiIcons.FiFolder />,
    mini: "R",
    collapse: true,

    component: AdminReports,
    views: [
      {
        path: "/dashboard/reports/compliance",
        component: AdminReports,
        layout: "/admin",
        name: "Compliance Reports",
      },
      {
        path: "/dashboard/reports/custom",
        component: CustomReports,
        layout: "/admin",
        name: "Custom Reports",
      },
      {
        path: "/dashboard/reports/custom/new",
        component: CustomReportForm,
        layout: "/admin",
        hidden: true,
        name: "Create Custom Report",
      },
    ],
  },
  {
    path: "/workflows",
    layout: "/admin",
    name: "Workflows",
    component: Workflows,
    roleRestrictions: [superAdminRole, regularAdminRole],
    hiddenChildRoutes: true,
    icon: <FiIcons.FiList />,
    mini: "O",
    views: [
      {
        path: "/workflows",
        component: Workflows,
        layout: "/admin",
      },
    ],
  },
  {
    path: "/approval",
    layout: "/admin",
    name: "Approvals",
    icon: <FiIcons.FiThumbsUp />,
    roleRestrictions: [superAdminRole, regularAdminRole],
    mini: "A",
    component: ApprovalTable,
    hiddenChildRoutes: true,
    views: [
      {
        path: "/approval",
        component: ApprovalTable,
        layout: "/admin",
        name: "Pending Approvals",
      },
      {
        path: "/approval/:type/:id",
        component: Approval,
        layout: "/admin",
      },
    ],
  },
  {
    path: "/dashboard/signup_codes",
    layout: "/admin",
    name: "Signup Codes",
    icon: <FiIcons.FiLink />,
    mini: "S",
    component: EmployerOrders,
    roleRestrictions: [regularAdminRole, salesAgentRole, superAdminRole],
  },
  {
    path: "/dashboard/paychex_dashboard",
    icon: <TbLetterP />,
    mini: "P",
    layout: "/admin",
    name: "Paychex",
    component: PaychexDashboard,
    roleRestrictions: [regularAdminRole, superAdminRole],
  },
];

export const filterAdminRoutesToRole = (roles: string[]) => {
  return adminRoutes.filter((route) => {
    if (route.roleRestrictions) {
      return some(roles, (role) => route.roleRestrictions!.includes(role));
    }
    return true;
  });
};

export default {
  employerRoutes,
  individualRoutes,
  adminRoutes,
};
